import { PureComponent } from 'react';

import Grid from '@mui/material/Grid';
import Portal from '@mui/material/Portal';
import { AlertColor, Box } from '@mui/material';

import AlertMessage from './AlertMessage';

import logger from '../../logger';

import { ALERT_DISPATCH_EVENTS, ALERT_RETURN_EVENTS, ALERT_EVENT_NAME } from '../../alertEvents';
import { ALERT_TYPES } from './alertTypes';
import EventBus from '../../EventBus';
import { AlertEvent, CancelConfirmEvent } from '../../alertDispatcher';
import { AlertsDisposeTimeout } from '../../db/local_storage';

interface AlertsProps {
  showAll: boolean;
  incrementAlertBadge: () => void;
  clearAlertBadge: () => void;
}

/**
 * History snapshot of an alert. Plain values only — no closures, no
 * callbacks, no setTimeout handles, no action functions. This is what the
 * sidebar (showAll=true) renders. The full AlertEvent only lives in
 * activeAlerts and is dropped on dispose.
 */
export interface AlertHistoryEntry {
  timestamp: number;
  type: string;
  message: string;
}

interface AlertsState {
  // Currently displayed alerts, with their full AlertEvent shape (timeouts,
  // confirm callbacks, action functions). Removed entirely on dispose so
  // their closures and timer handles are released immediately.
  activeAlerts: AlertEvent[];
  // Bounded log of past alerts as plain text snapshots. Used by the
  // sidebar/history view in MenuDrawer (showAll=true).
  history: AlertHistoryEntry[];
}

export class Alerts extends PureComponent<AlertsProps, AlertsState> {
  DISPOSE_TIME = AlertsDisposeTimeout.get();

  /**
   * Maximum number of entries kept in state.history. Each entry is a plain
   * snapshot (~80 bytes) so 500 is roughly ~40 KB. Large enough to scroll
   * back through a typical sampling shift; bounded so memory cannot grow
   * for the lifetime of the task.
   */
  static readonly MAX_ALERT_HISTORY = 500;

  constructor(props: AlertsProps) {
    super(props);

    this.state = {
      activeAlerts: [],
      history: [],
    };

    this.handleAlert = this.handleAlert.bind(this);
    this.disposeConfirm = this.disposeConfirm.bind(this);
    this.handleCancelAlert = this.handleCancelAlert.bind(this);
    this.disposeAlert = this.disposeAlert.bind(this);
  }

  componentDidMount() {
    EventBus.on(ALERT_EVENT_NAME, this.handleAlert);
    EventBus.on(ALERT_RETURN_EVENTS.CONFIRM_RETURN, this.handleCancelAlert);
  }

  componentDidUpdate(prevProps: AlertsProps) {
    if (!prevProps.showAll && this.props.showAll) {
      this.props.clearAlertBadge();
    }
  }

  componentWillUnmount() {
    EventBus.dispatch(ALERT_RETURN_EVENTS.UNMOUNT);
    EventBus.remove(ALERT_EVENT_NAME, this.handleAlert);
    EventBus.remove(ALERT_RETURN_EVENTS.CONFIRM_RETURN, this.handleCancelAlert);

    this.state.activeAlerts.forEach((alert) => {
      if (alert.timeoutId) {
        clearTimeout(alert.timeoutId);
      }
    });
  }

  /**
   * Append a sanitised snapshot of an alert to history. Plain values only:
   * the AlertEvent's actions, callbacks, timer handle and response arrays
   * are NOT copied across, so history entries hold no closures.
   *
   * Newest entry is index 0. Any prior history entry with the same message
   * is dropped first so a recurring alert moves to the top instead of
   * collecting duplicates. The history is then truncated to the cap.
   */
  private pushHistory(history: AlertHistoryEntry[], alert: AlertEvent): AlertHistoryEntry[] {
    const entry: AlertHistoryEntry = {
      timestamp: Date.now(),
      type: alert.type,
      message: alert.message,
    };
    const next = [entry, ...history.filter((h) => h.message !== alert.message)];

    if (next.length > Alerts.MAX_ALERT_HISTORY) {
      next.length = Alerts.MAX_ALERT_HISTORY;
    }

    return next;
  }

  addConfirmAlert(newAlert: AlertEvent) {
    const existing = this.state.activeAlerts.find((a) => a.active && a.message === newAlert.message);

    if (existing) {
      console.log('FOUND DUP', newAlert.confirmNum);
      EventBus.dispatch(ALERT_RETURN_EVENTS.CONFIRM_RETURN, {
        message: newAlert.message,
        confirm: false,
        confirmNum: newAlert.confirmNum,
      });

      // Still record this in history so the operator's notification log
      // reflects what they saw, even if the active confirm dialog is
      // already on screen.
      this.setState({ history: this.pushHistory(this.state.history, newAlert) });

      return;
    }

    newAlert.active = true;

    this.setState({
      activeAlerts: [newAlert, ...this.state.activeAlerts],
      history: this.pushHistory(this.state.history, newAlert),
    });
  }

  async disposeConfirm(message: string, confirm: boolean, confirmNum: number) {
    EventBus.dispatch(ALERT_RETURN_EVENTS.CONFIRM_RETURN, { message, confirm, confirmNum } as CancelConfirmEvent);
    this.disposeAlert(message);
  }

  addAlert(newAlert: AlertEvent) {
    const existing = this.state.activeAlerts.find((a) => a.active && a.message === newAlert.message);
    const timeoutId = setTimeout(() => this.disposeAlert(newAlert.message), newAlert.timeout ?? this.DISPOSE_TIME);

    if (existing) {
      // Restart the timer on the existing entry — don't add a duplicate
      // active alert. We still update history so a repeating alert moves
      // to the top of the sidebar log.
      clearTimeout(existing.timeoutId);
      existing.timeoutId = timeoutId;
      this.setState({
        activeAlerts: this.state.activeAlerts.slice(),
        history: this.pushHistory(this.state.history, newAlert),
      });

      return;
    }

    newAlert.timeoutId = timeoutId;
    newAlert.active = true;

    this.setState({
      activeAlerts: [newAlert, ...this.state.activeAlerts],
      history: this.pushHistory(this.state.history, newAlert),
    });
  }

  disposeAlert(message: string, tag?: string) {
    // Active alerts are removed entirely on dispose - clear their pending
    // timer and drop the AlertEvent so closures and confirm callbacks are
    // released immediately. The history (plain text) is untouched.
    const matchedByMessage = message
      ? this.state.activeAlerts.find((a) => a.active && a.message === message)
      : undefined;

    let next: AlertEvent[] | undefined;

    if (matchedByMessage) {
      clearTimeout(matchedByMessage.timeoutId);
      next = this.state.activeAlerts.filter((a) => a !== matchedByMessage);
    } else if (tag) {
      const tagged = this.state.activeAlerts.filter((a) => a.active && 'tag' in a && a.tag === tag);

      if (tagged.length === 0) {
        return;
      }

      tagged.forEach((a) => clearTimeout(a.timeoutId));
      next = this.state.activeAlerts.filter((a) => !tagged.includes(a));
    } else {
      return;
    }

    this.setState({ activeAlerts: next });
  }

  handleCancelAlert({ message, tag, confirm }: { message: string; tag: string; confirm: boolean }) {
    this.disposeAlert(message, tag);
  }

  async handleAlert(newAlert: AlertEvent) {
    // Fire-and-forget: don't block alert rendering on IndexedDB write.
    // On hardware with deprioritized disk I/O the await can stall.
    logger.log('ADDING_ALERT', newAlert);
    this.props.incrementAlertBadge();

    switch (newAlert.type) {
      case ALERT_DISPATCH_EVENTS.CONFIRM:
      case ALERT_DISPATCH_EVENTS.CONFIRM_WARN:
      case ALERT_DISPATCH_EVENTS.CONFIRM_ERROR:
        this.addConfirmAlert(newAlert);
        break;
      default:
        this.addAlert(newAlert);
        break;
    }
  }

  render() {
    const elementName = this.props.showAll ? 'allAlerts' : 'activeAlerts';
    const bottom = this.props.showAll ? '' : 0;
    const position = this.props.showAll ? 'relative' : 'fixed';
    const direction = this.props.showAll ? 'column' : 'row';

    return (
      <Portal container={document.getElementById(elementName)}>
        <Grid
          container
          spacing={1}
          // width:auto + margin:0 override MuiGrid-spacing-xs-1's `width: calc(100% + 8px)` / `margin: -4px`,
          // which would otherwise stretch the position:fixed container to 100vw and block clicks across the bottom row.
          style={{
            position: position,
            zIndex: 1600,
            bottom: bottom,
            display: 'inline-block',
            ...(this.props.showAll ? {} : { width: 'auto', margin: 0 }),
          }}
          direction={direction}
          size={12}
        >
          {this.props.showAll
            ? this.state.history.map((entry) => (
                <Grid key={entry.timestamp}>
                  <Grid container justifyContent="center" size={12}>
                    <AlertMessage
                      id={entry.timestamp}
                      category={ALERT_TYPES[entry.type] as AlertColor}
                      message={entry.message}
                      needsConfirm={false}
                      onConfirm={this.disposeConfirm}
                      showAll={this.props.showAll}
                    />
                  </Grid>
                </Grid>
              ))
            : this.state.activeAlerts.map((activeAlert, idx) => (
                <Grid key={idx}>
                  <Box sx={{ pointerEvents: 'auto', position: 'relative', margin: '30px' }}>
                    <AlertMessage
                      id={idx}
                      category={ALERT_TYPES[activeAlert.type] as AlertColor}
                      message={activeAlert.message}
                      needsConfirm={Boolean(activeAlert.needsConfirm)}
                      actions={activeAlert.actions}
                      onConfirm={this.disposeConfirm}
                      disposeAlert={this.disposeAlert}
                      confirmNum={activeAlert.confirmNum}
                      showAll={this.props.showAll}
                    />
                  </Box>
                </Grid>
              ))}
        </Grid>
      </Portal>
    );
  }
}

export default Alerts;
